import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

/**
 * Native <dialog>-Element-Wrapper.
 *
 * Vorteile gegenüber Custom-Modals:
 *  - eingebauter ESC-Handler, Backdrop-Klick optional
 *  - Top-Layer / Focus-Trap vom Browser
 *  - KEIN externes Dependency
 *
 * Caveat: showModal() schmiert auf alten Browsern (vor 2022) ab. Reicht hier.
 */

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
}

export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
}: DialogProps): JSX.Element {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dlg = ref.current;
    if (!dlg) return;
    if (open && !dlg.open) dlg.showModal();
    if (!open && dlg.open) dlg.close();
  }, [open]);

  useEffect(() => {
    const dlg = ref.current;
    if (!dlg) return;
    const handleClose = (): void => onClose();
    dlg.addEventListener('close', handleClose);
    return () => dlg.removeEventListener('close', handleClose);
  }, [onClose]);

  return (
    <dialog
      ref={ref}
      aria-labelledby="dialog-title"
      aria-describedby={description ? 'dialog-desc' : undefined}
      className="rounded-lg border border-border bg-surface p-0 text-text shadow-xl backdrop:bg-black/50 max-w-lg w-[90vw]"
      onClick={(event) => {
        // Klick auf Backdrop (event.target === dialog) schließt.
        if (event.target === ref.current) onClose();
      }}
    >
      <div className="flex flex-col gap-4 p-6">
        <header>
          <h2 id="dialog-title" className="text-lg font-semibold">
            {title}
          </h2>
          {description && (
            <p id="dialog-desc" className="mt-1 text-sm text-muted">
              {description}
            </p>
          )}
        </header>
        <div>{children}</div>
        {footer && <footer className="flex justify-end gap-2">{footer}</footer>}
      </div>
    </dialog>
  );
}
